import { hasDisplayText } from "./displayText.js";
import { stableAlignmentKey, uniqueStableAlignmentMap } from "./legalSections.js";

export const TYPE_LABELS = {
  RC: "ประมวลรัษฎากร",
  ACT: "พระราชบัญญัติแก้ไขเพิ่มเติม",
  ED: "พระราชกำหนดแก้ไขเพิ่มเติม",
  RD: "พระราชกฤษฎีกา",
  MR: "กฎกระทรวง",
  MFN: "ประกาศกระทรวงการคลัง",
  MFO: "คำสั่งกระทรวงการคลัง",
  BTR: "คำวินิจฉัยคณะกรรมการวินิจฉัยภาษีอากร",
  TP: "คำสั่งกรมสรรพากร ท.ป.",
  P: "คำสั่งกรมสรรพากร ป.",
  RDN: "ประกาศกรมสรรพากร",
  DGN: "ประกาศอธิบดีกรมสรรพากร",
  RDC: "ระเบียบและคำชี้แจงกรมสรรพากร",
  RULING: "ข้อหารือกรมสรรพากร",
  JUDGMENT: "คำพิพากษาศาลฎีกา",
  DTA: "อนุสัญญาภาษีซ้อน",
  DTA_PROTOCOL: "พิธีสารอนุสัญญาภาษีซ้อน",
};

export const TYPE_LABELS_EN = {
  RC: "Revenue Code",
  ACT: "Amending Act",
  ED: "Amending Emergency Decree",
  RD: "Royal Decree",
  MR: "Ministerial Regulation",
  MFN: "Ministry of Finance Notification",
  MFO: "Ministry of Finance Order",
  BTR: "Tax Ruling Committee Decision",
  TP: "Revenue Department Order (T.P.)",
  P: "Revenue Department Order (P.)",
  RDN: "Revenue Department Notification",
  DGN: "Director-General Notification",
  RDC: "Revenue Department Regulation or Guidance",
  RULING: "Revenue Department Ruling",
  JUDGMENT: "Supreme Court Judgment",
  DTA: "Double Tax Agreement",
  DTA_PROTOCOL: "Double Tax Agreement Protocol",
};

export function typeLabel(code, locale = "th") {
  return (locale === "en" ? TYPE_LABELS_EN[code] : TYPE_LABELS[code]) ||
    (locale === "en" ? "Other document" : "เอกสารอื่นๆ");
}

export const LEGAL_HIERARCHIES = [
  {
    id: "primary",
    title: "กฎหมายแม่บท",
    description: "ประมวลรัษฎากร พระราชบัญญัติ และพระราชกำหนด",
    codes: ["RC", "ACT", "ED"],
  },
  {
    id: "subordinate",
    title: "กฎหมายลำดับรอง",
    description: "พระราชกฤษฎีกา กฎกระทรวง และตราสารกระทรวงการคลัง",
    codes: ["RD", "MR", "MFN", "MFO"],
  },
  {
    id: "administrative",
    title: "ประกาศ คำสั่ง และแนวปฏิบัติ",
    description: "ตราสารของกรมสรรพากรและคณะกรรมการวินิจฉัยภาษีอากร",
    codes: ["BTR", "TP", "P", "RDN", "DGN", "RDC"],
  },
  {
    id: "interpretation",
    title: "ข้อหารือและคำพิพากษา",
    description: "ข้อหารือกรมสรรพากรและคำพิพากษาศาลฎีกา",
    codes: ["RULING", "JUDGMENT"],
  },
  {
    id: "international",
    title: "กฎหมายภาษีระหว่างประเทศ",
    description: "อนุสัญญาภาษีซ้อนและพิธีสาร",
    codes: ["DTA", "DTA_PROTOCOL"],
  },
];

const LEGAL_HIERARCHIES_EN = {
  primary: {
    title: "Primary legislation",
    description: "Revenue Code, Acts, and Emergency Decrees",
  },
  subordinate: {
    title: "Subordinate legislation",
    description: "Royal Decrees, Ministerial Regulations, and Ministry of Finance instruments",
  },
  administrative: {
    title: "Notifications, orders, and guidance",
    description: "Revenue Department and tax ruling committee instruments",
  },
  interpretation: {
    title: "Rulings and judgments",
    description: "Revenue Department rulings and Supreme Court judgments",
  },
  international: {
    title: "International tax law",
    description: "Double Tax Agreements and protocols",
  },
};

export function legalHierarchies(locale = "th") {
  if (locale !== "en") return LEGAL_HIERARCHIES;
  return LEGAL_HIERARCHIES.map((hierarchy) => ({
    ...hierarchy,
    ...LEGAL_HIERARCHIES_EN[hierarchy.id],
  }));
}

// Titles and source pages mirror the Revenue Department navigation. Content
// page selectors connect each branch to archived segments without inferring
// legal effect from the navigation labels.
export const REVENUE_CODE_STRUCTURE = {
  id: "revenue-code",
  title: "บทบัญญัติแห่งประมวลรัษฎากร",
  sourceUrl: "https://www.rd.go.th/674.html",
  children: [
    {
      id: "title-1",
      title: "ลักษณะ 1 ข้อความเบื้องต้น",
      sourceUrl: "https://www.rd.go.th/2600.html",
      contentUrl: "https://www.rd.go.th/5951.html",
    },
    {
      id: "title-2",
      title: "ลักษณะ 2 ภาษีอากรฝ่ายสรรพากร",
      sourceUrl: "https://www.rd.go.th/21456.html",
      children: [
        { id: "chapter-1", title: "หมวด 1 บทเบ็ดเสร็จทั่วไป", sourceUrl: "https://www.rd.go.th/2591.html", contentUrl: "https://www.rd.go.th/5950.html" },
        { id: "chapter-1-bis", title: "หมวด 1 ทวิ คณะกรรมการวินิจฉัยภาษีอากร", sourceUrl: "https://www.rd.go.th/2599.html", contentUrl: "https://www.rd.go.th/5949.html" },
        {
          id: "chapter-2",
          title: "หมวด 2 วิธีการเกี่ยวแก่ภาษีอากรประเมิน",
          sourceUrl: "https://www.rd.go.th/2598.html",
          children: [
            { id: "assessment-14-27", title: "มาตรา 14–27 จัตวา", contentUrl: "https://www.rd.go.th/5943.html" },
            { id: "assessment-28-37", title: "มาตรา 28–37 ตรี", contentUrl: "https://www.rd.go.th/5942.html" },
          ],
        },
        {
          id: "chapter-3",
          title: "หมวด 3 ภาษีเงินได้",
          sourceUrl: "https://www.rd.go.th/2597.html",
          children: [
            { id: "income-general", title: "ส่วน 1 ข้อความทั่วไป", sectionRange: [38, 39], contentUrl: "https://www.rd.go.th/5937.html" },
            { id: "income-individual", title: "ส่วน 2 การเก็บภาษีจากบุคคลธรรมดา", sectionRange: [40, 64], contentUrl: "https://www.rd.go.th/5937.html" },
            { id: "income-corporate", title: "ส่วน 3 การเก็บภาษีจากบริษัทและห้างหุ้นส่วนนิติบุคคล", sectionRange: [65, 76], contentUrl: "https://www.rd.go.th/5939.html" },
          ],
        },
        {
          id: "chapter-4",
          title: "หมวด 4 ภาษีมูลค่าเพิ่ม",
          sourceUrl: "https://www.rd.go.th/2596.html",
          children: [
            { id: "vat-77-79", title: "มาตรา 77–79/7", contentUrl: "https://www.rd.go.th/5205.html" },
            { id: "vat-80-82", title: "มาตรา 80–82/18", contentUrl: "https://www.rd.go.th/5206.html" },
            { id: "vat-83-84", title: "มาตรา 83–84/4", contentUrl: "https://www.rd.go.th/5207.html" },
            { id: "vat-85-86", title: "มาตรา 85–86/14", contentUrl: "https://www.rd.go.th/5208.html" },
            { id: "vat-87-90", title: "มาตรา 87–90/5", contentUrl: "https://www.rd.go.th/5209.html" },
          ],
        },
        { id: "chapter-5-sbt", title: "หมวด 5 ภาษีธุรกิจเฉพาะ", sourceUrl: "https://www.rd.go.th/2595.html", contentUrl: "https://www.rd.go.th/5204.html" },
        { id: "chapter-5-sign", title: "หมวด 5 ภาษีป้าย (ยกเลิก)", sourceUrl: "https://www.rd.go.th/2594.html" },
        { id: "chapter-6-stamp", title: "หมวด 6 อากรแสตมป์", sourceUrl: "https://www.rd.go.th/2593.html", contentUrl: "https://www.rd.go.th/5203.html" },
        { id: "chapter-7-entertainment", title: "หมวด 7 อากรมหรสพ (ยกเลิก)", sourceUrl: "https://www.rd.go.th/2592.html" },
      ],
    },
    { id: "title-3", title: "ลักษณะ 3 ภาษีบำรุงท้องที่ (ยกเลิก)", sourceUrl: "https://www.rd.go.th/2601.html" },
  ],
};

// Navigation captions, not replacement legal text. English chapter/part names
// follow the publisher's contents: https://www.rd.go.th/english/37748.html.
const REVENUE_CODE_LABELS_EN = {
  "revenue-code": "Revenue Code",
  "title-1": "Title 1 General provisions",
  "title-2": "Title 2 Revenue Taxes",
  "chapter-1": "Chapter 1 General provisions",
  "chapter-1-bis": "Chapter 1 Bis Commission of Taxation",
  "chapter-2": "Chapter 2 Procedures regarding assessment tax",
  "assessment-14-27": "Sections 14–27 quarter",
  "assessment-28-37": "Sections 28–37 ter",
  "chapter-3": "Chapter 3 Income Tax",
  "income-general": "Part 1 General Provisions",
  "income-individual": "Part 2 Personal Income Tax",
  "income-corporate": "Part 3 Corporate Income Tax",
  "chapter-4": "Chapter 4 Value Added Tax",
  "vat-77-79": "Sections 77–79/7",
  "vat-80-82": "Sections 80–82/18",
  "vat-83-84": "Sections 83–84/4",
  "vat-85-86": "Sections 85–86/14",
  "vat-87-90": "Sections 87–90/5",
  "chapter-5-sbt": "Chapter 5 Specific Business Tax",
  "chapter-5-sign": "Chapter 5 Signboard Tax (Repealed)",
  "chapter-6-stamp": "Chapter 6 Stamp Duty",
  "chapter-7-entertainment": "Chapter 7 Entertainment Duty (Repealed)",
  "title-3": "Title 3 Local Maintenance Tax (Repealed)",
};

export function revenueCodeNodeTitle(node, locale = "th") {
  return (locale === "en" && REVENUE_CODE_LABELS_EN[node.id]) || node.title;
}

export function segmentsForRevenueNode(node, segments, sourceSegments = segments) {
  if (!node.contentUrl && !node.sectionRange) return [];
  const page = (url) => String(url || "").split("#")[0];
  const sourceMembers = sourceSegments.filter((segment) => {
    if (node.contentUrl && page(segment.source_url) !== page(node.contentUrl)) return false;
    if (node.sectionRange) {
      const heading = segment.display_heading || segment.heading || "";
      const digits = segment.provision_id?.match(/\/section\/([0-9]+)/)?.[1]
        || heading.match(/(?:มาตรา|Section)\s*([0-9๐-๙]+)/i)?.[1];
      const number = Number(String(digits || "").replace(/[๐-๙]/g, digit => "๐๑๒๓๔๕๖๗๘๙".indexOf(digit)));
      if (!digits || number < node.sectionRange[0] || number > node.sectionRange[1]) return false;
    }
    return true;
  });
  if (sourceSegments === segments) return sourceMembers.filter(hasDisplayText);
  const sourceIds = uniqueStableAlignmentMap(sourceSegments);
  const targets = uniqueStableAlignmentMap(segments.filter(hasDisplayText));
  return sourceMembers.flatMap(segment => {
    const key = stableAlignmentKey(segment);
    const target = key && sourceIds.get(key) === segment && targets.get(key);
    return target ? [target] : [];
  });
}

export function buddhistYear(value, explicitYear) {
  if (explicitYear) return String(explicitYear);
  if (!value) return "ไม่ระบุปี";
  const thaiYear = String(value).match(/25\d{2}/)?.[0];
  if (thaiYear) return thaiYear;
  const year = Number(String(value).slice(0, 4));
  if (year >= 1800 && year <= 2200) return String(year + 543);
  return String(value).slice(0, 4);
}

export function displayDate(value, explicitYear, locale = "th") {
  if (locale === "en") {
    const year = gregorianYear(value, explicitYear);
    return year === "ไม่ระบุปี" ? "" : year;
  }
  const year = buddhistYear(value, explicitYear);
  return year === "ไม่ระบุปี" ? "" : `พ.ศ. ${year}`;
}

export function gregorianYear(value, explicitYear) {
  const candidate = explicitYear || value;
  if (!candidate) return "ไม่ระบุปี";
  const match = String(candidate).match(/(?:^|[^0-9])(\d{4})(?:[^0-9]|$)/);
  if (!match) return String(candidate).slice(0, 4);
  const year = Number(match[1]);
  return year >= 2400 && year <= 2700 ? String(year - 543) : String(year);
}

export function localizedDisplayDate(value, explicitYear, locale = "th") {
  return displayDate(value, explicitYear, locale);
}
